import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type Coupon = Tables<"coupons">;

export type CouponKind = "percent" | "amount" | "free_shipping";

export interface CouponDraft {
  code: string;
  description: string;
  kind: CouponKind;
  value: string;
  max_discount: string;
  min_order: string;
  starts_at: string;
  ends_at: string;
  max_uses: string;
  max_uses_per_customer: string;
  active: boolean;
}

export const VACIO: CouponDraft = {
  code: "",
  description: "",
  kind: "percent",
  value: "10",
  max_discount: "",
  min_order: "",
  starts_at: "",
  ends_at: "",
  max_uses: "",
  max_uses_per_customer: "1",
  active: true,
};

export const TIPOS: { id: CouponKind; label: string; ayuda: string }[] = [
  { id: "percent", label: "Porcentaje", ayuda: "Un % del subtotal, con tope opcional." },
  { id: "amount", label: "Monto fijo", ayuda: "Descuenta siempre la misma cantidad." },
  { id: "free_shipping", label: "Envío gratis", ayuda: "No descuenta la mercadería, solo el reparto." },
];

export async function fetchCoupons(organizationId: string): Promise<Coupon[]> {
  const { data, error } = await supabase
    .from("coupons")
    .select("*")
    .eq("organization_id", organizationId)
    .order("active", { ascending: false })
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/* ------------------------------------------------------------ conversiones */

const numero = (v: string): number | null => {
  const limpio = v.trim().replace(",", ".");
  if (limpio === "") return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
};

/** El dinero viaja como texto: numeric(12,2) no cabe en un number sin perder. */
const dinero = (v: string): string | null => {
  const n = numero(v);
  return n === null ? null : n.toFixed(2);
};

/** Un `datetime-local` vacio es null, no la fecha de hoy. */
const instante = (v: string): string | null =>
  v.trim() === "" ? null : new Date(v).toISOString();

const paraInput = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  // El input espera hora local sin zona, y toISOString devuelve UTC.
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

function aFila(organizationId: string, b: CouponDraft) {
  const esPorcentaje = b.kind === "percent";
  const esEnvio = b.kind === "free_shipping";

  return {
    organization_id: organizationId,
    code: b.code.trim().toUpperCase(),
    description: b.description.trim() || null,
    kind: b.kind,
    // La base rechaza un valor que no corresponda al tipo, asi que aqui se
    // manda null explicito en vez de arrastrar el numero del tipo anterior.
    value: esEnvio ? null : dinero(b.value),
    max_discount: esPorcentaje ? dinero(b.max_discount) : null,
    min_order: dinero(b.min_order) ?? "0.00",
    starts_at: instante(b.starts_at),
    ends_at: instante(b.ends_at),
    max_uses: numero(b.max_uses),
    max_uses_per_customer: numero(b.max_uses_per_customer) ?? 1,
    active: b.active,
  };
}

export async function createCoupon(organizationId: string, b: CouponDraft) {
  const { error } = await supabase.from("coupons").insert(aFila(organizationId, b));
  if (error) throw error;
}

export async function updateCoupon(id: string, organizationId: string, b: CouponDraft) {
  // El codigo no se edita: los pedidos ya hechos guardan una instantanea, y
  // cambiarlo dejaria el historial diciendo una cosa y el cupon otra.
  const { organization_id: _o, code: _c, ...cambios } = aFila(organizationId, b);
  const { error } = await supabase.from("coupons").update(cambios).eq("id", id);
  if (error) throw error;
}

export async function toggleCoupon(id: string, active: boolean) {
  const { error } = await supabase.from("coupons").update({ active }).eq("id", id);
  if (error) throw error;
}

export async function deleteCoupon(id: string) {
  const { error } = await supabase.from("coupons").delete().eq("id", id);
  if (error) throw error;
}

export function aBorrador(c: Coupon): CouponDraft {
  return {
    code: c.code,
    description: c.description ?? "",
    kind: c.kind as CouponKind,
    value: c.value === null ? "" : String(Number(c.value)),
    max_discount: c.max_discount === null ? "" : String(Number(c.max_discount)),
    min_order: Number(c.min_order) === 0 ? "" : String(Number(c.min_order)),
    starts_at: paraInput(c.starts_at),
    ends_at: paraInput(c.ends_at),
    max_uses: c.max_uses === null ? "" : String(c.max_uses),
    max_uses_per_customer: String(c.max_uses_per_customer),
    active: Boolean(c.active),
  };
}

export function validar(b: CouponDraft, nuevo: boolean): Record<string, string> {
  const e: Record<string, string> = {};

  if (nuevo) {
    const code = b.code.trim().toUpperCase();
    if (!code) e.code = "Escribe el código que va a teclear el comprador.";
    else if (!/^[A-Z0-9][A-Z0-9_-]{2,23}$/.test(code)) {
      e.code = "De 3 a 24 caracteres: letras, números, guion y guion bajo.";
    }
  }

  if (b.kind === "percent") {
    const v = numero(b.value);
    if (v === null || v <= 0 || v > 100) e.value = "Un porcentaje entre 1 y 100.";
  }

  if (b.kind === "amount") {
    const v = numero(b.value);
    if (v === null || v <= 0) e.value = "Indica cuánto descuenta.";
  }

  const tope = numero(b.max_discount);
  if (b.max_discount.trim() !== "" && (tope === null || tope <= 0)) {
    e.max_discount = "Déjalo vacío si no quieres tope.";
  }

  const min = numero(b.min_order);
  if (b.min_order.trim() !== "" && (min === null || min < 0)) {
    e.min_order = "Un monto válido, o vacío para no exigir mínimo.";
  }

  const usos = numero(b.max_uses);
  if (b.max_uses.trim() !== "" && (usos === null || usos < 1)) {
    e.max_uses = "Al menos 1, o vacío para no limitar.";
  }

  const porCliente = numero(b.max_uses_per_customer);
  if (porCliente === null || porCliente < 1) {
    e.max_uses_per_customer = "Al menos 1.";
  }

  if (b.starts_at && b.ends_at && new Date(b.ends_at) <= new Date(b.starts_at)) {
    e.ends_at = "La fecha de fin tiene que ser posterior al inicio.";
  }

  return e;
}

/** Lo que el comprador vería, dicho en una línea. */
export function resumen(c: Coupon, moneda: string): string {
  const dinero = (v: unknown) =>
    new Intl.NumberFormat("es-PE", { style: "currency", currency: moneda }).format(Number(v));

  if (c.kind === "free_shipping") return "Envío gratis";
  if (c.kind === "amount") return `${dinero(c.value)} de descuento`;

  const base = `${Number(c.value)}% de descuento`;
  return c.max_discount ? `${base}, hasta ${dinero(c.max_discount)}` : base;
}

/** Por que un cupon no esta funcionando ahora mismo. */
export function porQueNoAplica(c: Coupon): string | null {
  if (!c.active) return "Desactivado";

  const ahora = Date.now();
  if (c.starts_at && new Date(c.starts_at).getTime() > ahora) return "Aún no empieza";
  if (c.ends_at && new Date(c.ends_at).getTime() < ahora) return "Venció";
  if (c.max_uses !== null && c.times_used >= c.max_uses) return "Sin usos disponibles";

  return null;
}
